import React from "react";
import { Card } from "@/components/ui/card";
import {
  FuelIcon,
  MoneyIcon,
  DriverIcon,
  RpmIcon,
  RoutePath,
  CoinsIcon,
} from "./InlineIcons";
import { fmtMoney, fmtNumber, fmtRPM } from "@/lib/defaults";
import { CALC } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const Kpi = ({ label, value, hint, icon, testId, tone = "default" }) => {
  const toneClass =
    tone === "positive"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "negative"
        ? "text-red-600 dark:text-red-400"
        : "text-slate-900 dark:text-white";
  return (
    <Card
      data-testid={testId}
      className="kpi-card p-4 md:p-5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.12em] font-medium text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p
            className={`mt-2 text-2xl md:text-3xl font-semibold tabular leading-none ${toneClass}`}
          >
            {value}
          </p>
          {hint && (
            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 tabular">{hint}</p>
          )}
        </div>
        <div className="shrink-0 w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 grid place-items-center">
          {icon}
        </div>
      </div>
    </Card>
  );
};

export const MilesKpiRow = ({ lang, load, m }) => {
  const t = useT(lang);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <Kpi
        testId="kpi-loaded-miles"
        label={t("loadedMiles")}
        value={fmtNumber(load.loaded_miles)}
        icon={<RoutePath />}
      />
      <Kpi
        testId="kpi-empty-miles"
        label={t("emptyMiles")}
        value={fmtNumber(load.empty_miles)}
        icon={<RoutePath />}
      />
      <Kpi
        testId={CALC.kpiTotalMiles}
        label={t("totalMiles")}
        value={fmtNumber(m.total_miles)}
        icon={<RoutePath />}
      />
      <Kpi
        testId={CALC.kpiRealRpm}
        label={t("realRPM")}
        value={fmtRPM(m.real_rpm)}
        hint="Rate / Total Miles"
        icon={<RpmIcon />}
      />
    </div>
  );
};

export const ProfitKpiGrid = ({ lang, load, m }) => {
  const t = useT(lang);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
      <Kpi
        testId={CALC.kpiTotalRate}
        label={t("totalRate")}
        value={fmtMoney(load.rate)}
        icon={<MoneyIcon />}
      />
      <Kpi
        testId={CALC.kpiTripCost}
        label={t("tripCost")}
        value={fmtMoney(m.trip_cost)}
        icon={<FuelIcon />}
        tone="negative"
      />
      <Kpi
        testId={CALC.kpiDriverPay}
        label={t("driverPay")}
        value={fmtMoney(m.driver_pay)}
        icon={<DriverIcon />}
        tone="negative"
      />
      <Kpi
        testId={CALC.kpiDispatcherFee}
        label={t("dispatcherFeeAmt")}
        value={fmtMoney(m.dispatcher_fee)}
        icon={<CoinsIcon />}
        tone="negative"
      />
      <Kpi
        testId={CALC.kpiNetProfit}
        label={t("netProfit")}
        value={fmtMoney(m.net_profit)}
        icon={<MoneyIcon />}
        tone={m.net_profit >= 0 ? "positive" : "negative"}
      />
      <Kpi
        testId={CALC.kpiProfitPerMile}
        label={t("profitPerMile")}
        value={fmtRPM(m.profit_per_mile)}
        icon={<RpmIcon />}
        tone={m.profit_per_mile >= 0 ? "positive" : "negative"}
      />
    </div>
  );
};
